/**
 * A growable little/big-endian byte sink.
 *
 * Capacity doubles on demand so that serializing a dataset never needs its
 * final size known up front — which it is not, because a sequence's length is
 * only known once its items are encoded.
 */
export class ByteWriter {
    private buffer: ArrayBuffer;
    private view: DataView;
    private bytes: Uint8Array;
    private used = 0;

    constructor(initialCapacity = 64 * 1024) {
        this.buffer = new ArrayBuffer(Math.max(initialCapacity, 16));
        this.view = new DataView(this.buffer);
        this.bytes = new Uint8Array(this.buffer);
    }

    /** The number of bytes written so far. */
    get length(): number {
        return this.used;
    }

    private reserve(count: number): void {
        const needed = this.used + count;
        if (needed <= this.buffer.byteLength) {
            return;
        }
        let capacity = this.buffer.byteLength;
        while (capacity < needed) {
            capacity *= 2;
        }
        const grown = new ArrayBuffer(capacity);
        new Uint8Array(grown).set(this.bytes.subarray(0, this.used));
        this.buffer = grown;
        this.view = new DataView(grown);
        this.bytes = new Uint8Array(grown);
    }

    writeUint16(value: number, littleEndian: boolean): void {
        this.reserve(2);
        this.view.setUint16(this.used, value, littleEndian);
        this.used += 2;
    }

    writeUint32(value: number, littleEndian: boolean): void {
        this.reserve(4);
        this.view.setUint32(this.used, value, littleEndian);
        this.used += 4;
    }

    writeBytes(source: Uint8Array): void {
        this.reserve(source.byteLength);
        this.bytes.set(source, this.used);
        this.used += source.byteLength;
    }

    /** Writes each character's low byte. For ASCII literals such as a VR code. */
    writeAscii(text: string): void {
        this.reserve(text.length);
        for (let i = 0; i < text.length; i++) {
            this.bytes[this.used + i] = text.charCodeAt(i) & 0xff;
        }
        this.used += text.length;
    }

    writeRepeated(byte: number, count: number): void {
        this.reserve(count);
        this.bytes.fill(byte, this.used, this.used + count);
        this.used += count;
    }

    /** The written bytes as a new ArrayBuffer trimmed to length. */
    toArrayBuffer(): ArrayBuffer {
        return this.buffer.slice(0, this.used);
    }

    /** The written bytes as a view, valid until the next write. */
    toBytes(): Uint8Array {
        return this.bytes.subarray(0, this.used);
    }
}
